import { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Field,
  HStack,
  IconButton,
  Input,
  InputGroup,
  SimpleGrid,
  Switch,
  Text,
  VStack,
  Badge,
} from '@chakra-ui/react';
import { InputField, SectionCard, SelectInput } from '@themes/aegix';
import { useFormContext } from 'react-hook-form';
import {
  HiOutlineArrowPath,
  HiOutlineClipboardDocument,
} from 'react-icons/hi2';
import {
  ORGANIZATION_AUTH_DAYS_DEFAULT,
  ORGANIZATION_INDUSTRY_OPTIONS,
  ORGANIZATION_TYPE_OPTIONS,
  ORGANIZATION_STATUS_OPTIONS,
  type OrganizationFormValues,
} from '../../validations/organization.validation';
import {
  hasSsoAdminAccess,
  useSsoCapabilities,
} from '../../hooks/useSsoCapabilities';
import { toaster } from '../ui/toaster';
import type { DistrictBbox } from '../../services';
import type { OrganizationRow } from '../admin/hooks/useOrganizationsContentData';
import { useCountryStateDetails } from '../../hooks/useDistrictData';

/**
 * 256 bits of CSPRNG entropy, base64-encoded. Suitable for HS256.
 * Returns '' when window.crypto is unavailable (SSR / very old browser).
 *
 * Per ADR-098, HMAC-SHA-2 with a ≥256-bit key sourced from a CSPRNG is on
 * the approved cryptographic-primitives list. `crypto.getRandomValues` is
 * the Web Crypto API's CSPRNG; base64 is transport encoding only (entropy
 * preserved). The helper lives inline rather than in a util because it is
 * tightly coupled to the JWT Secret field UX below — moving it would force
 * an import roundtrip for no reuse.
 */
function generateJwtSecret(): string {
  if (typeof crypto === 'undefined' || !crypto.getRandomValues) return '';
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

/**
 * Add/Edit form for Districts.
 *
 * Sectioning mirrors the Django admin `OrganizationAdmin.fieldsets`
 * (server/apps/schools/admin.py:198) so SMS web stays visually parallel
 * to /admin and operators don't have to re-learn the layout.
 *
 * Surfaced sections: General, Alerts, Reports, Chat, Settings. The
 * Django `SSO Options` fieldset (roles, ignore_auto_provision) is still
 * omitted — those fields aren't on the SMS API surface yet.
 */
interface OrganizationsFormProps {
  currentRow?: OrganizationRow | null;
}

const OrganizationsForm: React.FC<OrganizationsFormProps> = ({
  currentRow,
}) => {
  const { t } = useTranslation();

  const industryOptions = useMemo(
    () =>
      ORGANIZATION_INDUSTRY_OPTIONS.map((o) => ({
        value: o.value,
        label: t(`org.industry.${o.value}`, o.label),
      })),
    [t],
  );

  const typeOptions = useMemo(
    () =>
      ORGANIZATION_TYPE_OPTIONS.map((o) => ({
        value: o.value,
        label: t(`org.type.${o.value}`, o.label),
      })),
    [t],
  );

  const statusOptions = useMemo(
    () =>
      ORGANIZATION_STATUS_OPTIONS.map((o) => ({
        value: o.value,
        label: t(`org.status.${o.value}`, o.label),
      })),
    [t],
  );

  const areaTypeOptions = useMemo(
    () => [
      { value: 'city', label: t('forms.areaTypeCity', 'City'), disabled: true },
      {
        value: 'county',
        label: t('forms.areaTypeCounty', 'County'),
        disabled: true,
      },
      { value: 'state', label: t('forms.areaTypeState', 'State') },
    ],
    [t],
  );
  const {
    watch,
    setValue,
    register,
    formState: { errors },
  } = useFormContext<OrganizationFormValues>();
  const values = watch();

  // Default to US for administrative location lookups (backend defaults to US).
  const {
    data: countryStateData,
    isLoading: isAreaLocationsLoading,
    isError: isAreaLocationsError,
  } = useCountryStateDetails('us');

  const countryStateOptions = useMemo<
    { label: string; value: string }[]
  >(() => {
    const items = countryStateData?.results?.items ?? [];
    return items.map((item) => ({
      label: item.name,
      value: item.name,
    }));
  }, [countryStateData]);

  const statesOptions = useMemo<{ label: string; value: string }[]>(() => {
    const val = values.area_name;
    if (val && !countryStateOptions.some((o) => o.value === val)) {
      return [{ label: val, value: val }, ...countryStateOptions];
    }
    return countryStateOptions;
  }, [values.area_name, countryStateOptions]);

  const getAreaNameOptions = useCallback(
    (areaType: string | undefined) => {
      switch (areaType) {
        case 'state':
          return statesOptions;
        // In the future, additional area types can be configured here:
        // case 'city':
        //   return cityOptions;
        // case 'county':
        //   return countyOptions;
        default:
          return [];
      }
    },
    [statesOptions],
  );

  const formatBbox = (bbox?: DistrictBbox | null): string => {
    if (!bbox || typeof bbox !== 'object') return 'N/A';
    const { min_lat, min_lng, max_lat, max_lng } = bbox;
    if (
      min_lat == null ||
      min_lng == null ||
      max_lat == null ||
      max_lng == null
    ) {
      return JSON.stringify(bbox);
    }
    return `[${Number(min_lat).toFixed(4)}, ${Number(min_lng).toFixed(4)}] to [${Number(max_lat).toFixed(4)}, ${Number(max_lng).toFixed(4)}]`;
  };

  const initialGeometry = useMemo(() => {
    const raw = currentRow?.raw;
    if (raw && (raw.center_lat || raw.boundary_geojson || raw.state)) {
      return {
        area_type: raw.area_type ?? '',
        area_name: raw.area_name ?? '',
        geometry: {
          center_lat: raw.center_lat,
          center_lng: raw.center_lng,
          bbox: raw.bbox,
          boundary_geojson: raw.boundary_geojson,
          state: raw.state,
        },
      };
    }
    return null;
  }, [currentRow?.raw]);

  const displayGeometry = useMemo(() => {
    if (
      initialGeometry &&
      values.area_type === initialGeometry.area_type &&
      values.area_name === initialGeometry.area_name
    ) {
      return initialGeometry.geometry;
    }
    return null;
  }, [initialGeometry, values.area_type, values.area_name]);
  const ssoCaps = useSsoCapabilities();
  /**
   * A non-SSO admin user is not permitted to flip the sandbox bit — sandbox
   * orgs receive relaxed security defaults (e.g. JWT secret auto-generation) and
   * prod data must never live in a sandbox org (FERPA).
   *
   * Backend DistrictViewSet is the authoritative gate — this is UX hardening,
   * not a security boundary.
   */
  // The Sandbox flag is an Aegix-internal-only concept (FERPA: prod data must
  // never live in a sandbox org). Hide the toggle from vendor users; only
  // Django staff / SSO admins can flip it. Backend `DistrictViewSet` is the
  // authoritative gate — this is UX hardening, not a security boundary.
  const canEditSandbox = hasSsoAdminAccess(ssoCaps.data);

  const handleCopyJwtSecret = async () => {
    const secret = values.jwt_secret ?? '';
    if (!secret) return;
    try {
      await navigator.clipboard.writeText(secret);
      toaster.create({
        title: t('forms.jwtSecretCopied', 'JWT secret copied'),
        type: 'success',
      });
    } catch {
      toaster.create({
        title: t('forms.failedToCopyJwtSecret', 'Failed to copy JWT secret'),
        type: 'error',
      });
    }
  };

  const handleRegenerateJwtSecret = () => {
    setValue('jwt_secret', generateJwtSecret(), { shouldDirty: true });
  };
  const coverageTypeOptions = [
    { label: 'Point Radius', value: 'point_radius' },
    { label: 'Admin Area', value: 'admin_area' },
  ];

  return (
    <VStack gap={5} align="stretch">
      <SectionCard title={t('common.general', 'General')}>
        <VStack gap={4} align="stretch">
          <Field.Root invalid={!!errors.name}>
            <Field.Label>{t('common.name', 'Name')}</Field.Label>
            <InputField
              placeholder={t(
                'forms.enterOrganizationNamePlaceholder',
                'Enter organization name',
              )}
              {...register('name')}
            />
            {errors.name && (
              <Field.ErrorText data-e2e="org-name-error">
                {errors.name.message as string}
              </Field.ErrorText>
            )}
          </Field.Root>

          <SimpleGrid columns={2} gap={4}>
            <Field.Root invalid={!!errors.domain}>
              <Field.Label>{t('admin.domain', 'Domain')}</Field.Label>
              <InputField placeholder="example.org" {...register('domain')} />
              {errors.domain && (
                <Field.ErrorText data-e2e="org-domain-error">
                  {errors.domain.message as string}
                </Field.ErrorText>
              )}
            </Field.Root>

            <Field.Root invalid={!!errors.industry}>
              <SelectInput
                label={t('admin.industry', 'Industry')}
                name="industry"
                value={values.industry ?? ''}
                onChange={(val: string) =>
                  setValue(
                    'industry',
                    (val || undefined) as OrganizationFormValues['industry'],
                  )
                }
                option={industryOptions}
                placeholder={t(
                  'forms.selectIndustryPlaceholder',
                  'Select industry',
                )}
              />
              {errors.industry && (
                <Field.ErrorText data-e2e="org-industry-error">
                  {errors.industry.message as string}
                </Field.ErrorText>
              )}
            </Field.Root>
          </SimpleGrid>

          <SimpleGrid columns={2} gap={4}>
            <Field.Root invalid={!!errors.type}>
              <SelectInput
                label={t('common.type', 'Type')}
                name="type"
                value={values.type ?? ''}
                onChange={(val: string) =>
                  setValue(
                    'type',
                    (val || undefined) as OrganizationFormValues['type'],
                  )
                }
                option={typeOptions}
                placeholder={t('forms.selectTypePlaceholder', 'Select type')}
              />
              {errors.type && (
                <Field.ErrorText data-e2e="org-type-error">
                  {errors.type.message as string}
                </Field.ErrorText>
              )}
            </Field.Root>

            <Field.Root invalid={!!errors.status}>
              <SelectInput
                label={t('common.status', 'Status')}
                name="status"
                value={values.status ?? ''}
                onChange={(val: string) =>
                  setValue(
                    'status',
                    (val || undefined) as OrganizationFormValues['status'],
                  )
                }
                option={statusOptions}
                placeholder={t(
                  'forms.selectStatusPlaceholder',
                  'Select status',
                )}
              />
              {errors.status && (
                <Field.ErrorText data-e2e="org-status-error">
                  {errors.status.message as string}
                </Field.ErrorText>
              )}
            </Field.Root>
          </SimpleGrid>

          {canEditSandbox && (
            <Field.Root>
              <Switch.Root
                checked={!!values.is_sandbox}
                onCheckedChange={(details) =>
                  setValue('is_sandbox', details.checked)
                }
              >
                <Switch.HiddenInput />
                <Switch.Control>
                  <Switch.Thumb />
                </Switch.Control>
                <Switch.Label>{t('forms.sandbox', 'Sandbox')}</Switch.Label>
              </Switch.Root>
              <Field.HelperText>
                {t(
                  'forms.sandboxHelperText',
                  'Aegix-internal only. Sandbox orgs must never hold production student data (FERPA).',
                )}
              </Field.HelperText>
            </Field.Root>
          )}
        </VStack>
      </SectionCard>

      <SectionCard title={t('forms.alerts', 'Alerts')}>
        <VStack gap={4} align="stretch">
          <SimpleGrid columns={2} gap={4}>
            <Field.Root>
              <Field.Label>
                {t('forms.startAlertButtonLabel', 'Start Alert Button Label')}
              </Field.Label>
              <InputField
                placeholder={t('forms.startAlertPlaceholder', 'Start Alert')}
                {...register('start_alert_button_label')}
              />
            </Field.Root>

            <Field.Root>
              <Field.Label>
                {t('forms.startDrillButtonLabel', 'Start Drill Button Label')}
              </Field.Label>
              <InputField
                placeholder={t('forms.startDrillPlaceholder', 'Start Drill')}
                {...register('start_drill_button_label')}
              />
            </Field.Root>

            <Field.Root>
              <Field.Label>
                {t('forms.startReportButtonLabel', 'Start Report Button Label')}
              </Field.Label>
              <InputField
                placeholder={t('forms.startReportPlaceholder', 'Start Report')}
                {...register('start_report_button_label')}
              />
            </Field.Root>
          </SimpleGrid>

          <Text fontSize="sm" color="text.secondary">
            {t(
              'forms.alertLabelsHelperText',
              "A button that appears always has a name, so these can't be set to empty. Leave one blank to keep the label as it is — a new organization gets the default shown above.",
            )}
          </Text>

          <Field.Root>
            <Switch.Root
              checked={!!values.is_silent_drill_default}
              onCheckedChange={(details) =>
                setValue('is_silent_drill_default', details.checked)
              }
            >
              <Switch.HiddenInput />
              <Switch.Control>
                <Switch.Thumb />
              </Switch.Control>
              <Switch.Label>
                {t('forms.silentDrillsByDefault', 'Silent drills by default')}
              </Switch.Label>
            </Switch.Root>
          </Field.Root>

          <Field.Root>
            <Switch.Root
              checked={!!values.can_share_alerts}
              onCheckedChange={(details) =>
                setValue('can_share_alerts', details.checked)
              }
            >
              <Switch.HiddenInput />
              <Switch.Control>
                <Switch.Thumb />
              </Switch.Control>
              <Switch.Label>
                {t(
                  'forms.canShareAlertsPeerOrgs',
                  'Can share alerts with peer organizations',
                )}
              </Switch.Label>
            </Switch.Root>
          </Field.Root>
        </VStack>
      </SectionCard>

      <SectionCard title={t('nav.reports', 'Reports')}>
        <Field.Root>
          <Switch.Root
            checked={!!values.use_missing_person_chat}
            onCheckedChange={(details) =>
              setValue('use_missing_person_chat', details.checked)
            }
          >
            <Switch.HiddenInput />
            <Switch.Control>
              <Switch.Thumb />
            </Switch.Control>
            <Switch.Label>
              {t('forms.enableMissingPersonChat', 'Enable missing-person chat')}
            </Switch.Label>
          </Switch.Root>
        </Field.Root>
      </SectionCard>

      <SectionCard title={t('forms.chat', 'Chat')}>
        <Field.Root invalid={!!errors.direct_chat_unread_notification_delay}>
          <Field.Label>
            {t(
              'forms.directChatDelayLabel',
              'Direct Chat Unread Notification Delay (seconds)',
            )}
          </Field.Label>
          <InputField
            type="number"
            placeholder="0"
            {...register('direct_chat_unread_notification_delay')}
          />
          <Field.HelperText>
            {t(
              'forms.directChatDelayHelperText',
              'Seconds to wait before sending a push notification for unread direct chat messages. Set to 0 to disable.',
            )}
          </Field.HelperText>
          {errors.direct_chat_unread_notification_delay && (
            <Field.ErrorText data-e2e="org-direct-chat-delay-error">
              {errors.direct_chat_unread_notification_delay.message as string}
            </Field.ErrorText>
          )}
        </Field.Root>
      </SectionCard>

      <SectionCard title={t('common.settings', 'Settings')}>
        <VStack gap={4} align="stretch">
          <SimpleGrid columns={2} gap={4}>
            <Field.Root invalid={!!errors.auth_expiry_days}>
              <Field.Label>
                {t('forms.authExpiryDays', 'Auth Expiry (days)')}
              </Field.Label>
              <InputField
                type="number"
                placeholder={String(ORGANIZATION_AUTH_DAYS_DEFAULT)}
                {...register('auth_expiry_days')}
              />
              {errors.auth_expiry_days && (
                <Field.ErrorText data-e2e="org-auth-expiry-error">
                  {errors.auth_expiry_days.message as string}
                </Field.ErrorText>
              )}
            </Field.Root>

            <Field.Root invalid={!!errors.auth_refresh_days}>
              <Field.Label>
                {t('forms.authRefreshDays', 'Auth Refresh (days)')}
              </Field.Label>
              <InputField
                type="number"
                placeholder={String(ORGANIZATION_AUTH_DAYS_DEFAULT)}
                {...register('auth_refresh_days')}
              />
              {errors.auth_refresh_days && (
                <Field.ErrorText data-e2e="org-auth-refresh-error">
                  {errors.auth_refresh_days.message as string}
                </Field.ErrorText>
              )}
            </Field.Root>
          </SimpleGrid>

          <Field.Root invalid={!!errors.system_admin_email}>
            <Field.Label>
              {t('forms.systemAdminEmails', 'System Admin Emails')}
            </Field.Label>
            <InputField
              placeholder="admin1@example.org, admin2@example.org"
              {...register('system_admin_email')}
            />
            <Field.HelperText>
              {t(
                'forms.systemAdminEmailsHelperText',
                'Comma-separated list of email addresses.',
              )}
            </Field.HelperText>
            {errors.system_admin_email && (
              <Field.ErrorText data-e2e="org-system-admin-email-error">
                {errors.system_admin_email.message as string}
              </Field.ErrorText>
            )}
          </Field.Root>

          <Field.Root invalid={!!errors.jwt_secret}>
            <Field.Label>
              {t('forms.jwtSecretPublicKey', 'JWT Secret / Public Key')}
            </Field.Label>
            <HStack gap={2} align="stretch">
              <Box flex="1">
                <InputGroup>
                  <Input
                    value={values.jwt_secret ?? ''}
                    onChange={(e) =>
                      setValue('jwt_secret', e.target.value, {
                        shouldDirty: true,
                      })
                    }
                    placeholder={t(
                      'forms.jwtSecretPlaceholder',
                      'HS256 shared secret, or RS256 public key (PEM)',
                    )}
                    fontFamily="mono"
                    fontSize="sm"
                  />
                </InputGroup>
              </Box>
              <IconButton
                aria-label={t(
                  'forms.copyJwtSecretAriaLabel',
                  'Copy JWT secret to clipboard',
                )}
                onClick={handleCopyJwtSecret}
                size="sm"
                variant="ghost"
                disabled={!values.jwt_secret}
              >
                <HiOutlineClipboardDocument />
              </IconButton>
              <IconButton
                aria-label={t(
                  'forms.regenerateJwtSecretAriaLabel',
                  'Regenerate JWT secret (HS256, 256-bit)',
                )}
                onClick={handleRegenerateJwtSecret}
                size="sm"
                variant="ghost"
              >
                <HiOutlineArrowPath />
              </IconButton>
            </HStack>
            <Field.HelperText>
              {t(
                'forms.jwtSecretHelperText',
                'Regenerate produces a 256-bit HS256 secret (browser CSPRNG, base64). Paste an RSA public key (PEM) here for RS256 instead.',
              )}
            </Field.HelperText>
            {errors.jwt_secret && (
              <Field.ErrorText data-e2e="org-jwt-secret-error">
                {errors.jwt_secret.message as string}
              </Field.ErrorText>
            )}
          </Field.Root>
          <VStack gap={4} align="stretch">
            <SelectInput
              label="Coverage Type"
              name="coverage_type"
              value={values.coverage_type ?? ''}
              onChange={(val: string) =>
                setValue(
                  'coverage_type',
                  (val || undefined) as OrganizationFormValues['coverage_type'],
                  { shouldDirty: true },
                )
              }
              option={coverageTypeOptions}
              placeholder="Select coverage type"
            />

            {values.coverage_type === 'point_radius' && (
              <SimpleGrid columns={3} gap={4}>
                <Field.Root invalid={!!errors.latitude}>
                  <Field.Label>Latitude</Field.Label>
                  <InputField placeholder="27.7172" {...register('latitude')} />
                  {errors.latitude && (
                    <Field.ErrorText>
                      {errors.latitude.message as string}
                    </Field.ErrorText>
                  )}
                </Field.Root>

                <Field.Root invalid={!!errors.longitude}>
                  <Field.Label>Longitude</Field.Label>
                  <InputField
                    placeholder="85.3240"
                    {...register('longitude')}
                  />
                  {errors.longitude && (
                    <Field.ErrorText>
                      {errors.longitude.message as string}
                    </Field.ErrorText>
                  )}
                </Field.Root>

                <Field.Root invalid={!!errors.service_radius_km}>
                  <Field.Label>Radius (km)</Field.Label>
                  <InputField
                    placeholder="20"
                    {...register('service_radius_km')}
                  />
                  {errors.service_radius_km && (
                    <Field.ErrorText>
                      {errors.service_radius_km.message as string}
                    </Field.ErrorText>
                  )}
                </Field.Root>
              </SimpleGrid>
            )}

            {values.coverage_type === 'admin_area' && (
              <VStack align="stretch" gap={4} width="100%">
                <SimpleGrid columns={2} gap={4}>
                  <Field.Root invalid={!!errors.area_type}>
                    <SelectInput
                      label="Area Type"
                      name="area_type"
                      value={values.area_type ?? ''}
                      onChange={(val: string) =>
                        setValue(
                          'area_type',
                          (val ||
                            undefined) as OrganizationFormValues['area_type'],
                          { shouldDirty: true },
                        )
                      }
                      option={areaTypeOptions}
                      placeholder="Select area type"
                    />
                    {errors.area_type && (
                      <Field.ErrorText>
                        {errors.area_type.message as string}
                      </Field.ErrorText>
                    )}
                  </Field.Root>

                  <Field.Root
                    invalid={!!errors.area_name || isAreaLocationsError}
                  >
                    <Field.Label>Area Name</Field.Label>
                    <HStack gap={2} width="100%" align="stretch">
                      <Box flex="1">
                        <SelectInput
                          name="area_name"
                          value={values.area_name ?? ''}
                          disabled={!values.area_type || isAreaLocationsLoading}
                          onChange={(val: string) =>
                            setValue('area_name', val, { shouldDirty: true })
                          }
                          option={getAreaNameOptions(values.area_type)}
                          placeholder={
                            isAreaLocationsLoading
                              ? 'Loading options...'
                              : values.area_type === 'state'
                                ? 'Select state'
                                : 'Select option'
                          }
                        />
                      </Box>
                    </HStack>
                    {isAreaLocationsError && (
                      <Field.ErrorText data-e2e="area-locations-error">
                        Failed to load administrative area options. Please try
                        again.
                      </Field.ErrorText>
                    )}
                    {errors.area_name && (
                      <Field.ErrorText>
                        {errors.area_name.message as string}
                      </Field.ErrorText>
                    )}
                  </Field.Root>
                </SimpleGrid>

                {displayGeometry &&
                  (displayGeometry.center_lat != null ||
                    Boolean(displayGeometry.boundary_geojson) ||
                    Boolean(displayGeometry.state)) && (
                    <Box
                      p={4}
                      borderWidth="1px"
                      borderColor="border.primary"
                      borderRadius="md"
                      bg="surface.secondary"
                      mt={2}
                    >
                      <Text
                        fontWeight="600"
                        fontSize="sm"
                        mb={3}
                        color="text.primary"
                      >
                        Resolved Geometry Details
                      </Text>
                      <SimpleGrid columns={2} gap={3} fontSize="xs">
                        <Box>
                          <Text color="text.secondary" fontWeight="500">
                            Center Coordinates
                          </Text>
                          <Text color="text.primary" mt={0.5}>
                            {displayGeometry.center_lat != null &&
                            displayGeometry.center_lng != null
                              ? `${Number(displayGeometry.center_lat)}, ${Number(displayGeometry.center_lng)}`
                              : 'Not resolved'}
                          </Text>
                        </Box>

                        <Box>
                          <Text color="text.secondary" fontWeight="500">
                            State
                          </Text>
                          <Text color="text.primary" mt={0.5}>
                            {displayGeometry.state || 'Not resolved'}
                          </Text>
                        </Box>

                        <Box>
                          <Text color="text.secondary" fontWeight="500">
                            Boundary Status
                          </Text>
                          <HStack gap={1.5} mt={0.5}>
                            <Badge
                              colorScheme={
                                displayGeometry.boundary_geojson
                                  ? 'green'
                                  : 'red'
                              }
                              variant="subtle"
                              fontSize="2xs"
                            >
                              {displayGeometry.boundary_geojson
                                ? 'GeoJSON Loaded'
                                : 'No Boundary'}
                            </Badge>
                          </HStack>
                        </Box>

                        {displayGeometry.bbox && (
                          <Box>
                            <Text color="text.secondary" fontWeight="500">
                              Bounding Box
                            </Text>
                            <Text
                              color="text.primary"
                              mt={0.5}
                              fontFamily="mono"
                              fontSize="2xs"
                            >
                              {formatBbox(displayGeometry.bbox)}
                            </Text>
                          </Box>
                        )}
                      </SimpleGrid>
                    </Box>
                  )}
              </VStack>
            )}
          </VStack>
        </VStack>
      </SectionCard>
    </VStack>
  );
};

export default OrganizationsForm;
